import { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { movies, categories } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    setActiveSlug(slug || 'all');
    window.scrollTo(0, 0);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  const filteredMovies = useMemo(() => {
    if (activeSlug === 'all') return movies;
    const categoryName = categories.find((c) => c.slug === activeSlug)?.name;
    if (!categoryName) return movies;
    return movies.filter((m) => m.category === categoryName);
  }, [activeSlug]);

  const pageTitle = activeSlug === 'all'
    ? '全部电影 - 555电影网高清免费电影在线观看'
    : `${activeCategory?.name || '电影'} - 555电影网高清免费电影在线观看`;

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo(0, 0);
  };

  const breadcrumbItems = [
    { name: '分类', path: '/category/all' },
    { name: activeCategory?.name || '全部' },
  ];

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://555dy.com/' },
      { '@type': 'ListItem', position: 2, name: '分类', item: 'https://555dy.com/category/all' },
      { '@type': 'ListItem', position: 3, name: activeCategory?.name || '全部' },
    ],
  };

  const webpageSchema = {
    name: pageTitle,
    description: `555电影网${activeCategory?.name || '全部'}电影分类，海量高清${activeCategory?.name || ''}电影免费在线观看，播放流畅不缓冲。`,
    url: `https://555dy.com/category/${activeSlug}`,
  };

  return (
    <>
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="WebPage" data={webpageSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <Breadcrumb items={breadcrumbItems} />

          {/* Category Header */}
          <section className="px-4 md:px-6 py-6 md:py-8">
            <div className="max-w-[1200px] mx-auto">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
                {activeSlug === 'all' ? '全部电影' : activeCategory?.name}
              </h1>

              {/* Category Tabs */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-700'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Movie Grid */}
          <section className="px-4 md:px-6 py-4 md:py-6">
            <div className="max-w-[1200px] mx-auto">
              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {filteredMovies.map((movie) => (
                    <Link key={movie.id} to={`/article/${movie.id}`} className="no-underline cursor-pointer" data-product-shop="">
                      <MovieCard {...movie} />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-movie-2-line text-2xl text-secondary-500"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无电影，请选择其他分类</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}